import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Picks up a new Vercel deployment without the user pressing refresh.
 *
 * Every build gives the main bundle a new hashed name (static/js/main.<hash>.js). We compare the
 * bundle this tab loaded with the one named in the latest app-shell.html. When they differ, the
 * page reloads on the user's next navigation, so nobody loses what they are typing on the current
 * screen. Form drafts kept in sessionStorage / router state survive the reload.
 *
 * The check is one small HTML fetch: every few minutes while the tab is visible, and when the
 * user comes back to the tab.
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_GAP_BETWEEN_CHECKS_MS = 60 * 1000;
const APP_SHELL_URL = '/app-shell.html';
const MAIN_BUNDLE_PATTERN = /\/static\/js\/main\.[a-f0-9]+\.js/;

// Bundle this tab is running. Null in local dev (no hashed bundle), which turns the check off.
function getLoadedBundlePath() {
  const scripts = document.querySelectorAll('script[src]');
  for (let i = 0; i < scripts.length; i++) {
    const match = scripts[i].getAttribute('src').match(MAIN_BUNDLE_PATTERN);
    if (match) {
      return match[0];
    }
  }
  return null;
}

async function fetchLatestBundlePath() {
  const response = await fetch(`${APP_SHELL_URL}?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) {
    return null;
  }
  const html = await response.text();
  const match = html.match(MAIN_BUNDLE_PATTERN);
  if (match) {
    return match[0];
  }
  return null;
}

export function useNewVersionReload() {
  const location = useLocation();
  const isNewVersionReadyRef = useRef(false);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const loadedBundlePath = getLoadedBundlePath();
    if (!loadedBundlePath) {
      return undefined;
    }

    let lastCheckTime = 0;
    let isStopped = false;

    const checkForNewVersion = async () => {
      if (isStopped || isNewVersionReadyRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastCheckTime < MIN_GAP_BETWEEN_CHECKS_MS) {
        return;
      }
      lastCheckTime = now;
      try {
        const latestBundlePath = await fetchLatestBundlePath();
        if (latestBundlePath && latestBundlePath !== loadedBundlePath) {
          isNewVersionReadyRef.current = true;
        }
      } catch (err) {
        // Offline or a network blip. Try again on the next check.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewVersion();
      }
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkForNewVersion();
      }
    }, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isStopped = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // A new version is waiting: load it as the user moves to another page.
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (isNewVersionReadyRef.current) {
      window.location.reload();
    }
  }, [location.pathname]);
}
